/**
 * activityTracker — batched user-activity telemetry (7-day retention).
 *
 * Singleton queue: track() enqueues, flush happens at FLUSH_SIZE events,
 * every FLUSH_INTERVAL_MS, or on visibilitychange→hidden (tab switch /
 * close). Telemetry must never break the app: send failures re-queue the
 * batch once and then drop it, and nothing here throws.
 *
 * sendBeacon is intentionally NOT used — the API needs the MSAL Bearer
 * header which beacons can't carry. The visibility flush uses the normal
 * authenticated client; it fires on every tab switch, so worst-case loss
 * is one flush window of a hard-killed tab.
 */
export type ActivityEventType = 'page_view' | 'search' | 'table_action' | 'pro_view' | 'click';

export interface ActivityEvent {
  event_type: ActivityEventType;
  occurred_at: string;
  page_path: string;
  target?: string | null;
  detail?: string | null;
  duration_ms?: number | null;
  session_id?: string | null;
}

export interface TrackInput {
  event_type: ActivityEventType;
  target?: string | null;
  detail?: string | null;
  duration_ms?: number | null;
  /** Override the captured route — used by page_view for the page just left. */
  page_path?: string;
}

interface TrackerOptions {
  send: (events: ActivityEvent[]) => Promise<void>;
  getPath?: () => string;
  isEnabled?: () => boolean;
  flushSize?: number;
  flushIntervalMs?: number;
}

const FLUSH_SIZE = 50;
const FLUSH_INTERVAL_MS = 10_000;

const newSessionId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export class ActivityTracker {
  private queue: ActivityEvent[] = [];
  private retriedOnce = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private readonly sessionId = newSessionId();

  private readonly send: TrackerOptions['send'];
  private readonly getPath: () => string;
  private readonly isEnabled: () => boolean;
  private readonly flushSize: number;
  private readonly flushIntervalMs: number;

  constructor(options: TrackerOptions) {
    this.send = options.send;
    this.getPath = options.getPath ?? (() => (typeof window !== 'undefined' ? window.location.pathname : ''));
    this.isEnabled = options.isEnabled ?? (() => true);
    this.flushSize = options.flushSize ?? FLUSH_SIZE;
    this.flushIntervalMs = options.flushIntervalMs ?? FLUSH_INTERVAL_MS;
  }

  track(input: TrackInput): void {
    try {
      if (!this.isEnabled()) return;
      this.queue.push({
        event_type: input.event_type,
        occurred_at: new Date().toISOString(),
        page_path: input.page_path ?? this.getPath(),
        target: input.target ?? null,
        detail: input.detail ?? null,
        duration_ms: input.duration_ms ?? null,
        session_id: this.sessionId,
      });
      this.ensureTimer();
      if (this.queue.length >= this.flushSize) {
        void this.flush();
      }
    } catch {
      // Telemetry never throws.
    }
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return;
    this.flushing = true;
    const batch = this.queue.splice(0, this.flushSize);
    try {
      await this.send(batch);
      this.retriedOnce = false;
    } catch {
      if (!this.retriedOnce) {
        // One retry: put the batch back at the front of the queue.
        this.retriedOnce = true;
        this.queue.unshift(...batch);
      } else {
        // Second failure: drop the batch, reset for future sends.
        this.retriedOnce = false;
      }
    } finally {
      this.flushing = false;
    }
  }

  /** Wire interval + visibility flushing. Call once from app bootstrap. */
  start(): void {
    this.ensureTimer();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void this.flush();
      });
    }
  }

  private ensureTimer(): void {
    if (this.timer !== null) return;
    this.timer = setInterval(() => {
      void this.flush();
    }, this.flushIntervalMs);
  }
}

// The wired app-wide singleton lives in activity.service.ts so this module
// stays free of api/MSAL imports (which touch window) and unit-testable.
